import { ChatItemTaskController } from './chatTaskInterface';
import { TaskObject } from '@varos/research-sdk-api';
import { ChatItemTaskViewProps, TaskStatusProps } from './chatTaskProps';
import { assertNever } from '@varos/util-typescript';
import { GoWorkflow } from 'react-icons/go';
import { ProgressProps } from '@chakra-ui/react';
import { ChatItemTaskAggregate } from './chatTaskModel';

export function createChatItemTaskController(): ChatItemTaskController {
    function getStatusProps(task: TaskObject): TaskStatusProps {
        if (task.status === 'in_progress' || task.status === 'pending') {
            return {
                colorScheme: 'blue',
                label: 'In Progress',
                tooltip: null,
            };
        }
        if (task.status === 'completed') {
            return {
                colorScheme: 'green',
                label: 'Completed',
                tooltip: null,
            };
        }
        if (task.status === 'failed') {
            return {
                colorScheme: 'red',
                label: 'Failed',
                tooltip: task.error
                    ? {
                          label: task.error,
                      }
                    : null,
            };
        }
        if (task.status === 'canceled') {
            return {
                colorScheme: 'gray',
                label: 'Canceled',
                tooltip: null,
            };
        }
        assertNever(task.status);
    }

    function getProgressProps(
        item: ChatItemTaskAggregate,
        task: TaskObject
    ): ProgressProps {
        const status = getStatusProps(task);
        const completeSteps = item.statuses?.filter(
            (status) => status.kind === 'completed'
        );
        const totalSteps = item.statuses?.length ?? 0;
        const progress = (completeSteps?.length ?? 0) / totalSteps;
        const isLoading =
            item.task?.status === 'in_progress' || item.task?.status === 'pending';
        return {
            value: Math.max(10, progress),
            min: 0,
            max: 100,
            colorScheme: status.colorScheme,
            hasStripe: isLoading,
            isAnimated: isLoading,
        };
    }
    return {
        useProps(context, item, props): ChatItemTaskViewProps {
            if (!item.task) {
                return {
                    Icon: GoWorkflow,
                    label: 'Starting task',
                    task: null,
                };
            }
            const status = getStatusProps(item.task);
            const progress = getProgressProps(item, item.task);

            const [firstStatus, ...restStatuses] = item.statuses ?? [];
            return {
                Icon: GoWorkflow,
                label: 'Task started',
                task: {
                    label: item.task.display_name,
                    status,
                    progress: progress,
                    step: {
                        label: firstStatus?.display_name ?? 'Step 1',
                    },
                    link: {
                        // TODO fix typing
                        to: props.task.path ?? '',
                    },
                },
            };
        },
    };
}
